import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { DutiesAndTaxesTaxType } from '@e-origin/shared';

const c = initContract();

export const GetTaricDescriptionSchema = z.object({
  harmonizedSystem: z
    .string()
    .regex(/^[0-9]*$/)
    .min(6)
    .max(10),
  nomenclatureCode: z
    .string()
    .regex(/^[0-9]*$/)
    .length(2)
    .optional(),
  taricCode: z
    .string()
    .regex(/^[0-9]*$/)
    .length(2)
    .optional(),
  originCode: z.string().optional(),
  isExport: z.preprocess((val) => val === 'true' || val === true, z.boolean().optional()),
  additionalCode: z.string().optional(),
  lang: z.string().optional().default('EN'),
});

export type GetTaricDescriptionCommand = z.infer<typeof GetTaricDescriptionSchema>;

export const TaricDescriptionResultSchema = z.object({
  hsCodeFound: z.boolean().optional(),
  newHsCodeFound: z.string().optional(),
  descriptionList: z.array(z.array(z.string().optional())),
  descriptionListException: z.array(z.array(z.string())),
  duties: z.record(z.nativeEnum(DutiesAndTaxesTaxType), z.array(z.string())).optional(),
  supplementaryUnits: z
    .array(
      z.object({
        codeQualifyer: z.string(),
        abbreviation: z.string().optional(),
      }),
    )
    .optional(),
  otherMeasures: z
    .array(
      z.object({
        originCode: z.string(),
        measureType: z.string(),
        duty: z.string(),
        typeCode: z.string(),
      }),
    )
    .optional()
    .default([]),
});

export type TaricDescriptionResult = z.infer<typeof TaricDescriptionResultSchema>;

export const UpdateTaricNomenclatureSchema = z.object({
  lang: z.string(),
  file: z.any(), // File object from express-fileupload,
});

export type UpdateTaricNomenclatureCommand = z.infer<typeof UpdateTaricNomenclatureSchema>;

export const PublishTaricNomenclatureSchema = z.object({
  lang: z.string(),
});

export type PublishTaricNomenclatureCommand = z.infer<typeof PublishTaricNomenclatureSchema>;

export const nomenclatureContract = c.router({
  getTaricDescriptionLists: {
    method: 'GET',
    path: '/external-api/taric-description-lists',
    query: GetTaricDescriptionSchema,
    responses: { 200: TaricDescriptionResultSchema },
  },
  updateTaricNomenclature: {
    method: 'POST',
    path: '/external-api/update-taric-nomenclature',
    body: UpdateTaricNomenclatureSchema,
    contentType: 'multipart/form-data',
    responses: { 200: z.boolean() },
  },
  publishTaricNomenclature: {
    method: 'POST',
    path: '/external-api/publish-taric-nomenclature',
    body: PublishTaricNomenclatureSchema,
    responses: { 200: z.boolean() },
  },
  getTaricDescription: {
    method: 'GET',
    path: '/external-api/taric-description',
    query: GetTaricDescriptionSchema,
    responses: { 200: TaricDescriptionResultSchema },
  },
  getTaricDutyRates: {
    method: 'GET',
    path: '/external-api/taric-duty-rates',
    query: z.object({
      harmonizedSystem: z.string(),
      nomenclatureCode: z.string(),
      taricCode: z.string(),
      originCode: z.string(),
    }),
    responses: { 200: z.array(z.string()) },
  },
});
